/**
 * Account-pair API client — the parent entity in the 3-level hedge
 * hierarchy (account_pair > pairs > trades).
 *
 *   GET    /api/account-pairs
 *   GET    /api/account-pairs/:slug
 *   POST   /api/account-pairs
 *   PATCH  /api/account-pairs/:slug/allowlist
 *   POST   /api/account-pairs/:slug/{ai-mode,ai-paused,close,symbols}
 */

import type {
  AccountPairListItem,
  AccountPairDetailRes,
  CreateAccountPairReq,
  AddSymbolPairReq,
  PairMode,
  SwapLegReq,
} from "@pfh/shared";
import { api } from "../api";

export type {
  AccountPairListItem,
  AccountPairSide,
  AccountPairDetailRes,
} from "@pfh/shared";

/**
 * Status filter maps 1:1 to the worker's ?status= query param.
 * active    — status != closed (default)
 * archived  — status = closed AND archived = 1 (reopenable)
 * dissolved — status = closed AND archived = 0 (irreversible, legs freed)
 * all       — no filter
 */
export type AccountPairStatusFilter =
  | "active"
  | "archived"
  | "dissolved"
  | "all";

export async function listAccountPairs(
  status?: AccountPairStatusFilter,
): Promise<AccountPairListItem[]> {
  const qs = status && status !== "active" ? `?status=${status}` : "";
  const res = await api.get<{ accountPairs: AccountPairListItem[] }>(
    `/api/account-pairs${qs}`,
  );
  return res.accountPairs;
}

export async function getAccountPair(slug: string): Promise<AccountPairDetailRes> {
  return api.get<AccountPairDetailRes>(
    `/api/account-pairs/${encodeURIComponent(slug)}`,
  );
}

export async function createAccountPair(
  body: CreateAccountPairReq,
): Promise<{ id: string; slug: string }> {
  const res = await api.post<{ ok: true; id: string; slug: string }>(
    "/api/account-pairs",
    body,
  );
  return { id: res.id, slug: res.slug };
}

export async function updateAllowlist(
  slug: string,
  symbolAllowlist: string[],
): Promise<string[]> {
  const res = await api.patch<{ ok: true; symbolAllowlist: string[] }>(
    `/api/account-pairs/${encodeURIComponent(slug)}/allowlist`,
    { symbolAllowlist },
  );
  return res.symbolAllowlist;
}

/**
 * POST /api/account-pairs/:slug/ai-mode — set the canonical AI mode at
 * the account_pair level. Cascades to children with NULL mode_override;
 * pinned children stay pinned (sticky).
 */
export async function setAccountPairAiMode(
  slug: string,
  aiMode: PairMode,
): Promise<{ ok: true; aiMode: PairMode }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/ai-mode`, { aiMode });
}

/**
 * POST /api/account-pairs/:slug/ai-paused — operator kill-switch. When
 * paused=true the heartbeat gate skips Haiku for every child regardless
 * of mode. Resuming restores whatever mode each child resolves to.
 */
export async function setAccountPairAiPaused(
  slug: string,
  paused: boolean,
): Promise<{ ok: true; paused: boolean }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/ai-paused`, { paused });
}

/**
 * POST /api/account-pairs/:slug/activate — operator clicked "Complete
 * Setup" on the paused-for-setup banner. Flips the pair out of the
 * setup gate (status: 'paused' → 'active'), clears the AI kill-switch,
 * and promotes any child symbol pairs still in 'setup' to 'active'.
 */
export async function activateAccountPair(
  slug: string,
): Promise<{ ok: true; parentActivated: boolean; activatedChildren: number }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/activate`, {});
}

/**
 * POST /api/account-pairs/:slug/mode — flip the pair sleep controller
 * between auto (presence-driven) and manual (operator pinned).
 */
export async function setPairMode(
  slug: string,
  mode: "auto" | "manual",
): Promise<{ ok: true; mode: "auto" | "manual"; desired: "awake" | "asleep" | null }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/mode`, { mode });
}

/** POST /api/account-pairs/:slug/sleep — close both legs' Brave windows. Manual mode only. */
export async function sleepPair(
  slug: string,
): Promise<{ ok: true; desired: "awake" | "asleep" | null }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/sleep`, {});
}

/** POST /api/account-pairs/:slug/wake — open both legs' Brave windows. Manual mode only. */
export async function wakePair(
  slug: string,
): Promise<{ ok: true; desired: "awake" | "asleep" | null }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/wake`, {});
}

/**
 * POST /api/account-pairs/:slug/force-sleep — operator escalation when
 * a normal sleep left a leg orphan. Sends `account:force-kill` to every
 * orphan leg's bot, which SIGKILLs the worker process (and Brave with
 * it). Manual mode only. Returns the count of dispatched force-kills. */
export async function forceSleepPair(
  slug: string,
): Promise<{ ok: true; dispatched: number }> {
  return api.post(`/api/account-pairs/${encodeURIComponent(slug)}/force-sleep`, {});
}

/**
 * POST /api/account-pairs/:slug/auto-sleep-protected — toggle whether
 * the auto-cycler is allowed to sleep this pair when a position is
 * open AND both SL+TP are set on the broker side. Default true.
 */
export async function setPairAutoSleepProtected(
  slug: string,
  enabled: boolean,
): Promise<{ ok: true; autoSleepProtected: boolean }> {
  return api.post(
    `/api/account-pairs/${encodeURIComponent(slug)}/auto-sleep-protected`,
    { enabled },
  );
}

/**
 * POST /api/dashboard/presence — bulk presence ping. Called every
 * 30 s by `use-presence-pinger` while a pair-context route is
 * foregrounded so the cycler keeps both legs awake while the
 * operator is actively viewing.
 */
export async function postDashboardPresence(
  pairIds: string[],
): Promise<{ ok: true; count: number }> {
  return api.post(`/api/dashboard/presence`, { pairIds });
}

/**
 * Archive = status=closed, archived=1, accounts.pair_id KEPT.
 * The worker endpoint is still /close for back-compat; the semantic is
 * archive. Pair can be reopened via reopenAccountPair.
 */
export async function archiveAccountPair(slug: string): Promise<void> {
  await api.post(`/api/account-pairs/${encodeURIComponent(slug)}/close`, {});
}

export async function reopenAccountPair(slug: string): Promise<{ slug: string }> {
  const res = await api.post<{ ok: true; slug: string }>(
    `/api/account-pairs/${encodeURIComponent(slug)}/reopen`,
    {},
  );
  return { slug: res.slug };
}

export async function swapAccountPairLeg(
  slug: string,
  body: SwapLegReq,
): Promise<{ ok: true; quality: string; deltas: unknown[] }> {
  return api.post(
    `/api/account-pairs/${encodeURIComponent(slug)}/swap-leg`,
    body,
  );
}

export interface RematchAllTally {
  scanned: number;
  paired: number;
  proposalsCreated: number;
  alreadyPaired: number;
  errors: number;
}

export async function rematchAllAccountPairs(): Promise<RematchAllTally> {
  const res = await api.post<{ ok: true } & RematchAllTally>(
    "/api/account-pairs/rematch-all",
    {},
  );
  return {
    scanned: res.scanned,
    paired: res.paired,
    proposalsCreated: res.proposalsCreated,
    alreadyPaired: res.alreadyPaired,
    errors: res.errors,
  };
}

export interface RebuildImperfectTally {
  dissolved: number;
  paired: number;
  proposalsCreated: number;
  stillUnpaired: number;
  errors: number;
}

/**
 * Tear down every non-perfect pair and rebuild from scratch. One-click
 * power-tool for the case where a batch of accounts was paired before
 * the operator had the correct DD/profit-target rules entered — once
 * the rules are fixed on the accounts, this recycles the pool so the
 * perfect matches surface without dissolving one-by-one.
 */
export async function rebuildImperfectAccountPairs(): Promise<RebuildImperfectTally> {
  const res = await api.post<{ ok: true } & RebuildImperfectTally>(
    "/api/account-pairs/rebuild-imperfect",
    {},
  );
  return {
    dissolved: res.dissolved,
    paired: res.paired,
    proposalsCreated: res.proposalsCreated,
    stillUnpaired: res.stillUnpaired,
    errors: res.errors,
  };
}

export async function addSymbolPair(
  slug: string,
  body: AddSymbolPairReq,
): Promise<{ id: string; slug: string }> {
  const res = await api.post<{ ok: true; id: string; slug: string }>(
    `/api/account-pairs/${encodeURIComponent(slug)}/symbols`,
    body,
  );
  return { id: res.id, slug: res.slug };
}

/**
 * Edit a pair's target lots. The actual trade size is still clamped to
 * min(target, long.sustainable, short.sustainable) — this is the upper
 * bound the operator wants to trade.
 */
export async function updatePairLots(
  accountPairSlug: string,
  pairSlug: string,
  lots: number,
): Promise<{ lots: number }> {
  const res = await api.patch<{ ok: true; lots: number }>(
    `/api/account-pairs/${encodeURIComponent(accountPairSlug)}/pairs/${encodeURIComponent(pairSlug)}/lots`,
    { lots },
  );
  return { lots: res.lots };
}
